import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { useCreateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { useDeleteResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useDeleteResource';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { useSelection } from '@cube3/state/src/redux/components/useSelection';
import { useCallback, useMemo } from 'react';

export const getNodeIdFromSelection = (selection) => {
  if (selection) {
    return selection.type === 'project'
      ? selection.relationships?.node.id
      : selection.id;
  }
};

export const useFavoriteActions = (
  clearItems?,
  clearActiveItem?,
  selectionOverride?,
  filterOverride?
) => {
  const { selection, visiting } = useSelection();

  const [workspaceId] = useCurrentWorkspace();
  const [accountId] = useCurrentAccount(true);
  const selectedItems = selectionOverride
    ? selectionOverride
    : selection?.length
    ? selection
    : [visiting];

  const { resources: favoritedItems } = useResourceList__ALPHA(
    useMemo(() => {
      return {
        resourceType: 'account',
        resourceId: accountId,
        edgeType: 'favorite',
        edgeLabel: 'favorites',
        params: {
          filter: {
            node_id: filterOverride
              ? filterOverride
              : selectedItems
                  ?.map((t) => getNodeIdFromSelection(t))
                  .join(',') || ''
          },
          page: {
            size: -1
          }
        }
      };
    }, [selectedItems, accountId])
  );

  const option =
    selectedItems.filter((s) =>
      favoritedItems?.find(
        (b) => b.attributes.node_id === getNodeIdFromSelection(s)
      )
    )?.length *
      2 >=
    selectedItems?.length
      ? 'Unfavorite'
      : 'Favorite';

  const [createFavorite] = useCreateResource__ALPHA({
    resourceType: 'favorite',
    ancestor: { type: 'account', id: accountId },
    relationship: 'favorites',
    cacheInvalidator: useCallback(
      (r) => [
        r,
        {
          type: 'account' as const,
          id: accountId,
          relationship: 'favorites'
        },
        {
          type: 'workspace',
          id: workspaceId,
          relationship: 'search'
        }
      ],
      [accountId, workspaceId]
    )
  });

  const [deleteFavorite] = useDeleteResource__ALPHA({
    cacheInvalidator: useCallback(
      (r) => [
        r,
        { type: 'workspace', id: workspaceId, relationship: 'search' }
      ],
      [workspaceId]
    )
  });
  const filterItems = useCallback(
    (items, toRemove) => {
      if (!items?.length) return null;
      if (toRemove) {
        // return the favorited items
        return items
          .map((s) =>
            favoritedItems?.find(
              (b) => b.attributes.node_id === getNodeIdFromSelection(s)
            )
          )
          .filter(Boolean);
      }
      // return the items that are not favorited
      return items.filter(
        (s) =>
          !favoritedItems?.find(
            (b) => b.attributes.node_id === getNodeIdFromSelection(s)
          )
      );
    },
    [favoritedItems]
  );

  const handleAdd = useCallback(
    (items) => {
      if (!items?.length) return;
      const toAdd = filterItems(items, false);
      toAdd.forEach((item) => {
        createFavorite({
          type: 'favorite',
          node_id: getNodeIdFromSelection(item)
        });
      });
    },
    [createFavorite]
  );

  const handleDelete = useCallback(
    (items) => {
      if (!items?.length) return;
      items.forEach((item) => {
        deleteFavorite({
          type: 'favorite',
          id: item.id
        });
      });
      clearItems && clearItems();
      clearActiveItem && clearActiveItem();
    },

    [deleteFavorite, clearItems, clearActiveItem]
  );

  const onClick = useCallback(() => {
    if (!selectedItems?.length) return;
    const items = filterItems(selectedItems, option === 'Unfavorite');

    if (option === 'Unfavorite') {
      handleDelete(items);
    } else {
      handleAdd(items);
    }
  }, [filterItems, handleAdd, handleDelete, option, selectedItems]);

  return {
    onAddToFavorites: handleAdd,
    onClick,
    type: option
  } as {
    onAddToFavorites: (items) => void;
    onClick: () => void;
    type: 'Favorite' | 'Unfavorite';
  };
};
